import React from 'react';
import {
  Box, TextField, Checkbox, FormControlLabel, Typography,
  Paper, Stack, Divider, useTheme,
} from '@mui/material';

import type { AdmissionField, AdmissionFormSection } from '../../../types/admissions';
import { fmtClassName } from '../../../utils/pdf/format';
import { isCnicFieldKey, isPhotoField } from './admissionFormFields';
import PersonPhotoInput from '../../people/PersonPhotoInput';
import SearchableSelect from '../../common/SearchableSelect';
import SearchableMultiSelect from '../../common/SearchableMultiSelect';
import { cnicDigits, formatCnic } from '../../../utils/cnic';

export interface ClassOption { _id: string; name: string }

interface Props {
  /** Snapshot of cycle.formTemplate.sections or a template.sections. */
  sections: AdmissionFormSection[];
  values: Record<string, any>;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  onChange: (key: string, value: any) => void;
  onBlur?: (key: string) => void;
  /** Options for the special-cased `classId` system field. */
  classOptions?: ClassOption[];
  /** When true, each section renders as its own card (public form).
   *  When false, sections stack flat (walk-in dialog). Default true. */
  cardSections?: boolean;
  /** Section key → extra element rendered after the section's fields
   *  (e.g. CNIC dedupe status). Keeps special-case UI out of the renderer. */
  sectionFooters?: Partial<Record<string, React.ReactNode>>;
  /** Field key → live hint shown under the input in place of its help
   *  text (e.g. the child's age under the date of birth). Errors win. */
  fieldHints?: Partial<Record<string, React.ReactNode>>;
  /** Disable all inputs (e.g. during submit). */
  disabled?: boolean;
  /** Hide fields with these keys even if enabled on the template.
   *  Used by walk-in re-admission mode to skip father/mother sections. */
  hiddenKeys?: ReadonlySet<string>;
  /** Flat mode only: render each section's title caption. Default true;
   *  hosts that draw their own section headers turn it off. */
  showSectionTitles?: boolean;
}

/**
 * The single component that turns a template snapshot into form inputs.
 * Shared by PublicApplyPage and WalkInApplicationDialog so operator-defined
 * fields never need to be wired twice.
 *
 * Required fields are marked with the standard asterisk only. Non-system
 * (custom) fields flow into AdmissionApplication.formData.
 *
 * Each field is wrapped in `[data-field-key]` so a host can scroll the first
 * invalid field into view.
 */
const DynamicFieldRenderer: React.FC<Props> = ({
  sections, values, errors = {}, touched = {}, onChange, onBlur,
  classOptions = [], cardSections = true, sectionFooters, fieldHints, disabled, hiddenKeys,
  showSectionTitles = true,
}) => {
  const theme = useTheme();

  const renderSection = (section: AdmissionFormSection) => {
    const visibleFields = section.fields
      .filter(f => f.enabled !== false)
      .filter(f => !hiddenKeys?.has(f.key));
    if (visibleFields.length === 0) return null;

    const grid = (
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
        columnGap: 2,
        rowGap: 1.5,
      }}>
        {visibleFields.map(field => (
          <Box
            key={field.key}
            data-field-key={field.key}
            sx={{
              gridColumn: fullWidthField(field) ? { sm: 'span 2' } : undefined,
            }}
          >
            {isPhotoField(field) ? (
              // Bottom space matches the helper-text line under the inputs.
              <Box sx={{ pb: 2.5 }}><PersonPhotoInput
                label={field.label}
                name={values.name}
                value={values[field.key] ?? undefined}
                onChange={(file) => { onChange(field.key, file ?? undefined); onBlur?.(field.key); }}
                required={field.required}
                error={touched[field.key] ? errors[field.key] : undefined}
                disabled={disabled}
              /></Box>
            ) : <FieldInput
              field={field}
              value={values[field.key]}
              error={touched[field.key] ? errors[field.key] : undefined}
              onChange={(v) => onChange(field.key, v)}
              onBlur={() => onBlur?.(field.key)}
              classOptions={classOptions}
              hint={fieldHints?.[field.key]}
              disabled={disabled}
            />}
          </Box>
        ))}
      </Box>
    );

    const footer = sectionFooters?.[section.key];

    if (cardSections) {
      return (
        <Paper
          key={section.key}
          variant="outlined"
          sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 2 }}
        >
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>{section.title}</Typography>
          <Divider sx={{ mb: 2.5 }} />
          {grid}
          {footer && (
            <Box sx={{ mt: 2 }}>{footer}</Box>
          )}
        </Paper>
      );
    }

    return (
      <Box key={section.key}>
        {showSectionTitles && <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
            display: 'block',
            mb: 1,
          }}
        >
          {section.title}
        </Typography>}
        {grid}
        {footer && <Box sx={{ mt: 1.5 }}>{footer}</Box>}
      </Box>
    );
  };

  return (
    <Stack spacing={cardSections ? 2.5 : 3} sx={{ color: theme.palette.text.primary }}>
      {sections.map(renderSection)}
    </Stack>
  );
};

export default DynamicFieldRenderer;

// ─── Field input dispatcher ────────────────────────────────────────────────

interface FieldInputProps {
  field: AdmissionField;
  value: any;
  error?: string;
  onChange: (v: any) => void;
  onBlur?: () => void;
  classOptions: ClassOption[];
  hint?: React.ReactNode;
  disabled?: boolean;
}

const FieldInput: React.FC<FieldInputProps> = ({
  field, value, error, onChange, onBlur, classOptions, hint, disabled,
}) => {
  const helper = error || field.helpText || ' ';
  const commonTF = {
    label: field.label,
    required: field.required,
    error: !!error,
    helperText: (error || hint || helper) as React.ReactNode,
    placeholder: field.placeholder,
    size: 'small' as const,
    fullWidth: true,
    disabled,
    onBlur,
    // Labels always float, matching the searchable selects beside them.
    InputLabelProps: { shrink: true },
  };

  // ─── Choice fields use the portal's searchable select ─────────────────
  if (field.key === 'classId' || field.type === 'dropdown') {
    const options = field.key === 'classId'
      ? classOptions.map(c => ({ value: c._id, label: fmtClassName(c.name) }))
      : (field.options || []).map(o => ({ value: o.value, label: o.label }));
    return (
      <SearchableSelect
        label={field.required ? `${field.label} *` : field.label}
        value={value || ''}
        onChange={(v) => { onChange(v); onBlur?.(); }}
        options={options}
        placeholder={field.key === 'classId' && options.length === 0 ? 'No classes open' : 'Select'}
        clearable={!field.required}
        disabled={disabled}
        error={!!error}
        helperText={helper}
        fullWidth
        minWidth={0}
      />
    );
  }

  // ─── Special-case: CNICs normalize to digits and cap at 13 ─────────────
  const isCnic = isCnicFieldKey(field.key);
  if (isCnic) {
    return (
      <TextField
        {...commonTF}
        value={formatCnic(value)}
        onChange={(e) => onChange(cnicDigits(e.target.value))}
        placeholder={field.placeholder || '35202-1234567-1'}
        inputProps={{ inputMode: 'numeric', maxLength: 15 }}
        InputProps={{ sx: { fontFamily: 'monospace', letterSpacing: 0.5 } }}
      />
    );
  }

  switch (field.type) {
    case 'textarea':
      return (
        <TextField
          {...commonTF}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          multiline
          minRows={2}
        />
      );
    case 'number':
      return (
        <TextField
          {...commonTF}
          type="number"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{
            min: field.validation?.min,
            max: field.validation?.max,
            step: 'any',
          }}
        />
      );
    case 'email':
      return (
        <TextField
          {...commonTF}
          type="email"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'phone':
      return (
        <TextField
          {...commonTF}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          inputProps={{ inputMode: 'tel' }}
        />
      );
    case 'date':
      return (
        <TextField
          {...commonTF}
          type="date"
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    case 'boolean':
      return (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!value}
              onChange={(e) => onChange(e.target.checked)}
              disabled={disabled}
            />
          }
          label={
            <Typography variant="body2">
              {field.label}
              {field.required && <span style={{ color: '#d32f2f', marginLeft: 2 }}>*</span>}
            </Typography>
          }
        />
      );
    case 'multiselect':
      return (
        <SearchableMultiSelect
          label={field.required ? `${field.label} *` : field.label}
          values={Array.isArray(value) ? value : []}
          onChange={(v) => { onChange(v); onBlur?.(); }}
          options={(field.options || []).map(o => ({ value: o.value, label: o.label }))}
          placeholder="Select"
          disabled={disabled}
          error={!!error}
          helperText={helper}
          fullWidth
          minWidth={0}
        />
      );
    case 'text':
    default:
      return (
        <TextField
          {...commonTF}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
        />
      );
  }
};

// Which field types take the whole row rather than half.
const fullWidthField = (f: AdmissionField): boolean => {
  if (f.type === 'textarea') return true;
  if (f.type === 'multiselect') return true;
  if (f.type === 'boolean') return true;
  if (f.key === 'address') return true;
  if (isPhotoField(f)) return true;
  return false;
};
